import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import apiClient from "../api/apiClient";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, SearchIcon } from "./icons";
import { Tip } from "./StudioControls";
import { useDesignThumbnail } from "./designThumbnails";
import { isPublicListing, listingAccessAllowed, listingImage, listingPrice, listingSubtitle, listingTitle } from "./listings";
import { useInfiniteSentinel, useListingFeed } from "./useListingFeed";

const TABS = [
  ["suggested", "Suggested", "Explore mockups you might like"],
  ["mine", "My mockups", "Browse mockups you already own"],
  ["creator", "Same creator", "Explore more mockups from this mockup's creator"],
];

/** Debounced copy of a value. */
export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** The signed-in user's My mockups folders (root first). */
export function useFolders(enabled = true) {
  const [folders, setFolders] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    apiClient.get("/folders/").then((result) => {
      if (!cancelled && result.ok) setFolders(Array.isArray(result.data) ? result.data : result.data?.results || []);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return folders;
}

/** `design` (optional): show the user's design on the mockup (designThumbnails.js). */
export function ListingCard({ listing, owned, picked = false, note, onClick, label, design = null }) {
  const price = listingPrice(listing);
  const title = listingTitle(listing);
  const [imageRef, designSrc] = useDesignThumbnail(listing, design);
  return (
    <button type="button" className={`sw-card${picked ? " is-picked" : ""}`} onClick={onClick} aria-pressed={picked || undefined} aria-label={label || `Preview ${title}`}>
      <span className="sw-card-image" ref={imageRef}>
        <img src={designSrc || listingImage(listing)} alt="" loading="lazy" />
        {owned ? (
          <span className="sw-owned-mark" title="In My mockups">
            <CheckIcon />
          </span>
        ) : (
          <span className="sw-price-badge">{price.label}</span>
        )}
        {note && <span className="sw-card-note">{note}</span>}
      </span>
      <span className="sw-card-info">
        <strong>{title}</strong>
        <span>{listingSubtitle(listing)}</span>
      </span>
    </button>
  );
}

/**
 * Discover: Suggested, My mockups and Same creator, with search and infinite
 * scroll. Mockups already in the editor are left out. With a `design`, cards
 * show it on each mockup.
 */
export default function DiscoverPanel({ creatorId, inEditor, design, onOpen, search, onSearch, searchRef, collapsed, onCollapse, onExpand, drawerOpen, onCloseDrawer }) {
  const { role, userid } = useSelector((state) => state.auth);
  const isAdmin = role === "A";
  const [tab, setTab] = useState("suggested");
  const [folderId, setFolderId] = useState(null);
  const query = useDebounced(search.trim());
  const folders = useFolders(tab === "mine");
  const filter = useMemo(() => (tab === "creator" ? (l) => isPublicListing(l, userid) : null), [tab, userid]);
  const feed = useListingFeed({ tab, search: query, creatorId, folderId: tab === "mine" ? folderId : null, filter });
  const visible = feed.items.filter((l) => !inEditor.has(String(l.id)));
  const sentinel = useInfiniteSentinel(feed.loadMore, [feed.items.length, feed.hasMore, tab, collapsed]);

  if (collapsed) {
    return (
      <section className="sw-panel sw-discover is-collapsed" aria-label="Discover">
        <button type="button" className="sw-folded" onClick={onExpand} aria-expanded="false" aria-label="Expand Discover">
          <ChevronLeftIcon />
          <span>Discover</span>
        </button>
      </section>
    );
  }

  let empty = null;
  if (!feed.loading && !visible.length) {
    if (feed.error) empty = feed.error;
    else if (tab === "creator" && !creatorId) empty = "Open a mockup to see more from its creator.";
    else if (query) empty = "No mockups match. Try another search.";
    else if (tab === "mine") empty = folderId ? "No mockups in this folder." : "Mockups you buy or create appear here.";
    else empty = "No more mockups to show.";
  }

  return (
    <section className={`sw-panel sw-discover${drawerOpen ? " is-drawer-open" : ""}`} aria-label="Discover">
      <div className="sw-panel-heading sw-discover-heading">
        <h2>Discover</h2>
        <button type="button" className="sw-collapse-button sw-desktop-only" onClick={onCollapse} aria-expanded="true" aria-label="Collapse Discover">
          <ChevronRightIcon />
        </button>
        <button type="button" className="sw-icon-button sw-drawer-only" onClick={onCloseDrawer} aria-label="Close Discover">
          <CloseIcon />
        </button>
      </div>
      <label className="sw-search">
        <SearchIcon />
        <input ref={searchRef} type="search" placeholder="Search mockups…" aria-label="Search mockups" value={search} onChange={(e) => onSearch(e.target.value)} />
      </label>
      <div className="sw-tabs" role="tablist" aria-label="Mockup collections">
        {TABS.map(([key, label, help]) => (
          <Tip key={key} title={help}>
            <button type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
              {label}
            </button>
          </Tip>
        ))}
      </div>
      {tab === "mine" && folders.length > 0 && (
        <div className="sw-chips" role="group" aria-label="Folders">
          <button type="button" aria-pressed={!folderId} onClick={() => setFolderId(null)}>
            All
          </button>
          {folders.map((f) => (
            <button key={f.id} type="button" aria-pressed={folderId === f.id} onClick={() => setFolderId(f.id)}>
              {f.name}
            </button>
          ))}
        </div>
      )}
      <div className="sw-discover-grid" role="tabpanel">
        {visible.map((listing) => (
          <ListingCard key={listing.id} listing={listing} owned={tab === "mine" || listingAccessAllowed(listing, { isAdmin, userid })} design={design} onClick={() => onOpen(listing)} />
        ))}
        {empty && <p className="sw-empty">{empty}</p>}
        {feed.loading && <SkeletonCards count={visible.length ? 2 : 6} />}
        <div ref={sentinel} className="sw-sentinel" aria-hidden="true" />
      </div>
    </section>
  );
}

export function SkeletonCards({ count }) {
  return Array.from({ length: count }, (_, i) => (
    <span key={`s${i}`} className="sw-card is-skeleton" aria-hidden="true">
      <span className="sw-card-image" />
      <span className="sw-card-info">
        <strong />
        <span />
      </span>
    </span>
  ));
}
